import React from 'react';
import {
  LuCircleCheck,
  LuInbox,
  LuRefreshCw,
  LuTriangleAlert,
} from 'react-icons/lu';
import './UnifiedState.css';

const STATE_ICONS = {
  empty: LuInbox,
  error: LuTriangleAlert,
  success: LuCircleCheck,
};

const UnifiedState = ({
  type = 'empty',
  title,
  message,
  actionLabel = 'Retry',
  onAction,
  compact = false,
  className = '',
  rows = 3,
  layout = 'list',
  illustration,
  illustrationAlt = '',
  actionIcon: ActionIcon = LuRefreshCw,
}) => {
  const normalizedType = ['loading', 'empty', 'error', 'success'].includes(type)
    ? type
    : 'empty';
  const Icon = STATE_ICONS[normalizedType];
  const classes = [
    'unified-state',
    `unified-state--${normalizedType}`,
    compact ? 'unified-state--compact' : '',
    className,
  ].filter(Boolean).join(' ');

  if (normalizedType === 'loading') {
    const normalizedLayout = ['list', 'table', 'cards'].includes(layout) ? layout : 'list';
    return (
      <div className={classes} role="status" aria-live="polite" aria-label={title || 'Loading'}>
        <div
          className={`unified-state-skeleton unified-state-skeleton--${normalizedLayout}`}
          aria-hidden="true"
        >
          {normalizedLayout === 'cards' ? (
            <>
              <span className="unified-state-skeleton-title" />
              <div className="unified-state-skeleton-cards">
                {Array.from({ length: Math.max(2, Math.min(rows, 4)) }, (_, index) => (
                  <span key={index} className="unified-state-skeleton-card" />
                ))}
              </div>
            </>
          ) : (
            <>
              {normalizedLayout === 'list' && <span className="unified-state-skeleton-icon" />}
              <span className="unified-state-skeleton-title" />
              {Array.from({ length: Math.max(1, rows) }, (_, index) => (
                <span
                  key={index}
                  className="unified-state-skeleton-line"
                  style={{
                    '--unified-skeleton-width': normalizedLayout === 'table'
                      ? '100%'
                      : `${Math.max(42, 86 - (index * 13))}%`,
                  }}
                />
              ))}
            </>
          )}
        </div>
        {title && <span className="unified-state-loading-label">{title}</span>}
      </div>
    );
  }

  return (
    <div
      className={classes}
      role={normalizedType === 'error' ? 'alert' : 'status'}
      aria-live={normalizedType === 'error' ? 'assertive' : 'polite'}
    >
      {illustration ? (
        <img
          className="unified-state-illustration"
          src={illustration}
          alt={illustrationAlt}
        />
      ) : Icon && (
        <span className="unified-state-icon" aria-hidden="true">
          <Icon />
        </span>
      )}
      <div className="unified-state-copy">
        {title && <h3>{title}</h3>}
        {message && <p>{message}</p>}
      </div>
      {onAction && (
        <button type="button" className="unified-state-action" onClick={onAction}>
          <ActionIcon aria-hidden="true" />
          {actionLabel}
        </button>
      )}
    </div>
  );
};

export default UnifiedState;
